import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { EncounterCard } from "@/components/EncounterCard";
import { LogOut, User as UserIcon, Camera, Trophy, Save, MapPin } from "lucide-react";
import { toast } from "sonner";

function Avatar({ url, name, size = 80 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.45 }}>
            {initial}
        </div>
    );
}

export default function Profile() {
    const { user, loading, logout, checkAuth } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [myEncs, setMyEncs] = useState([]);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [bio, setBio] = useState("");
    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    const [reputation, setReputation] = useState(0);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user) return;
        setName(user.name || "");
        setBio(user.bio || "");
        setLocation(user.location || "");
        setAvatarUrl(user.avatar_url || user.picture || null);
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/social/users/${user.id}`);
                if (!cancelled) setReputation(r.data.user?.reputation || 0);
            } catch (_) { /* ignore */ }
            try {
                const r2 = await api.get("/encounters/mine/list", { params: { limit: 100 } });
                if (!cancelled) setMyEncs(r2.data.items || []);
            } catch (_) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    if (!user) return null;

    async function saveProfile() {
        setSaving(true);
        try {
            const { data } = await api.patch("/social/users/me", { name, bio, location });
            toast.success("Profile saved");
            setAvatarUrl(data.avatar_url || avatarUrl);
            checkAuth && checkAuth();
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    }

    async function onAvatarPick(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post("/social/users/me/avatar", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAvatarUrl(data.avatar_url);
            toast.success("Avatar updated");
            checkAuth && checkAuth();
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="ok-container py-10 max-w-5xl mx-auto" data-testid="profile-page">
            <div className="ok-card p-8 flex flex-col md:flex-row items-start gap-6">
                <div className="relative">
                    <Avatar url={avatarUrl} name={user.name} size={96} />
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-[var(--ok-accent)] text-black flex items-center justify-center hover:scale-105 transition shadow-lg"
                        data-testid="avatar-upload-btn"
                        aria-label="Change avatar"
                    >
                        <Camera className="w-4 h-4" />
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onAvatarPick} className="hidden" data-testid="avatar-file-input" />
                </div>

                <div className="flex-1 min-w-0 space-y-3 w-full">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="ok-input w-full font-display text-2xl"
                        placeholder="Display name"
                        data-testid="profile-name-input"
                    />
                    <div className="font-mono text-xs opacity-60">{user.email}</div>
                    <div className="relative">
                        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40 pointer-events-none" />
                        <input
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="ok-input w-full text-sm pl-9"
                            placeholder="Location (e.g. Perth, Australia)"
                            maxLength={120}
                            data-testid="profile-location-input"
                        />
                    </div>
                    <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="Tell other researchers about yourself: areas of interest, expertise, investigation specialties, personal experiences, credentials, contact preferences. Line breaks and paragraphs are preserved."
                        className="ok-input w-full text-sm leading-relaxed"
                        rows={6}
                        maxLength={2500}
                        data-testid="profile-bio-input"
                    />
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest opacity-50 -mt-1">
                        <span>Line breaks preserved · Markdown not supported</span>
                        <span data-testid="profile-bio-counter" className={bio.length > 2400 ? "text-[var(--ok-accent)]" : ""}>
                            {bio.length} / 2500
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button onClick={saveProfile} disabled={saving} className="ok-btn-primary text-sm" data-testid="profile-save-btn">
                            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
                        </button>
                        <div className="ok-tag inline-flex items-center gap-1" data-testid="profile-reputation-badge">
                            <Trophy className="w-3 h-3" />
                            <strong className="text-[var(--ok-accent)]">{reputation}</strong>
                            <span>reputation</span>
                        </div>
                        {user.subscription_tier && user.subscription_tier !== "free" ? (
                            <div className="ok-tag inline-flex" style={{ borderColor: "var(--ok-accent)", color: "var(--ok-accent)" }} data-testid="profile-tier-badge">
                        {user.subscription_tier === "research_council" ? "Research Council" : "Verified Witness"}
                                {user.founding_member ? " · Founding" : ""}
                            </div>
                        ) : (
                            <Link to="/pricing" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-upgrade-link">
                                Free · Become a Founding Witness →
                            </Link>
                        )}
                        <Link to={`/u/${user.id}`} className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-public-link">
                            View public profile →
                        </Link>
                        <Link to="/my-submissions" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-my-submissions-link">
                            My submissions →
                        </Link>
                        <Link to="/me/media" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-my-media-link">
                            My media →
                        </Link>
                        <Link to="/me/analytics" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-analytics-link">
                            Analytics →
                        </Link>
                        <Link to="/collections" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-collections-link">
                            Collections →
                        </Link>
                        <Link to="/messages" className="ok-tag inline-flex hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid="profile-messages-link">
                            Messages →
                        </Link>
                        <button onClick={logout} className="ok-btn-outline ml-auto text-sm" data-testid="profile-logout">
                            <LogOut className="w-4 h-4" /> Log out
                        </button>
                    </div>
                </div>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3">Your encounters ({myEncs.length})</div>
            {myEncs.length === 0 ? (
                <div className="ok-card p-8 text-center">
                    <UserIcon className="w-8 h-8 mx-auto opacity-30 mb-2" />
                    <p className="opacity-70 mb-4">You haven&apos;t logged any encounters yet.</p>
                    <Link to="/lodge" className="ok-btn-primary inline-flex" data-testid="profile-lodge-btn">Lodge your first encounter</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myEncs.map((e) => <EncounterCard key={e.id} e={e} />)}
                </div>
            )}
        </div>
    );
}
